import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SpaceRole, UserRole } from '../model/business-role.model';
import { BusinessRolesService } from './business-roles.service';
import { ItsService } from './its.service';
import { ContactSubscriptionService } from './contact-subscriptions.service';
import { ApplicationsService } from './applications.service';
import { UserDialogService } from './user-dialog.service';
import { UserDialogStage } from '../../dialogs/user-dialog/user-dialog.component';
import { RoleToDelete } from '../../dialogs/submit-roles-dialog/submit-roles-dialog.component';
import { ContactSubscriptionPost } from '../model/user.model';
import { ActiveLocationRolesComponent } from '../../user-form/active-location-roles/active-location-roles.component';
import { LocationRolesComponent } from '../../user-form/location-roles/location-roles.component';
import { SubscriptionTableComponent } from '../../shared/components/subscription-table/subscription-table.component';

@Injectable({
  providedIn: 'root',
})
export class UserFormRolesService {
  private activeLocationRoles$ = new BehaviorSubject<ActiveLocationRolesComponent>(null);
  private configureLocationRoles$ = new BehaviorSubject<LocationRolesComponent>(null);
  private subscriptionTable$ = new BehaviorSubject<SubscriptionTableComponent>(null);

  public hasAnyVoiceNotificationRole = false;
  public hasAnySMSNotificationRole = false;
  public hasAnyEmailNotificationRole = false;
  public hasAnyOfferNotificationRole = false;
  public hasAnyPerformanceNotificationRole = false;
  public hasDrUser = false;
  public unsafeSubscriptions = true;
  public syncTooltip = 'user.sync_to_ecrm_tooltip';

  constructor(
    private businessRolesService: BusinessRolesService,
    private itsService: ItsService,
    private contactSubscriptionService: ContactSubscriptionService,
    private applicationsService: ApplicationsService,
    private userDialogService: UserDialogService,
  ) {}

  async applyRoles(userId: string, state: boolean): Promise<SpaceRole[]> {
    if (!state) {
      return await this.businessRolesService.getUserRoles(userId);
    }
    return [];
  }

  hasRole(roles: any[], searchRole: string): boolean {
    return roles
      .map(({ businessRole, name }: any) => {
        businessRole = businessRole || name;
        return businessRole;
      })
      .includes(searchRole);
  }

  hasCommunityUserRole(userRoles: UserRole[]): boolean {
    const role = 'COMMUNITY_USER';
    if (userRoles && userRoles.length && this.hasRole(userRoles, role)) {
      return true;
    }
    return false;
  }

  async submitRoles(userId: string, userData: any, bulkRoles?: boolean): Promise<void> {
    const { activeLocationRoles, configureLocationRoles } = this.getRequiredComponents();

    if (!activeLocationRoles || !configureLocationRoles) {
      throw new Error('Required components not initialized');
    }

    return this.submitRolesImpl(userId, activeLocationRoles, configureLocationRoles, userData, bulkRoles);
  }

  async submitSubs(userId: string, userData: any): Promise<void> {
    const { subscriptionTable } = this.getRequiredComponents();

    if (!subscriptionTable) {
      throw new Error('Subscription table component not initialized');
    }

    return this.submitSubsImpl(userId, userData, subscriptionTable);
  }

  private async submitRolesImpl(
    userId: string,
    activeLocationRoles: ActiveLocationRolesComponent,
    configureLocationRoles: LocationRolesComponent,
    userData: any,
    bulkRoles?: boolean,
  ): Promise<void> {
    if (bulkRoles !== undefined && bulkRoles === true) activeLocationRoles.sendUpdatingToDialog();
    else activeLocationRoles.errorRoles([]);

    const submittedRolesStatus = new Map();
    const errors = [];
    const spaceRolesToSubmit = [];
    const allNewRoles = [];
    let subPromise;

    activeLocationRoles.newSpacesWithRoles.forEach((space) => {
      space.newRoles.forEach((role: any) =>
        spaceRolesToSubmit.push(JSON.stringify({ role: role.name, entityId: space.id, entityType: space.spaceType })),
      );
      allNewRoles.push(space);
    });

    activeLocationRoles.spacesWithRoles
      .filter((space) => space.newRoles.length > 0)
      .forEach((space) => {
        space.newRoles.forEach((role: any) =>
          spaceRolesToSubmit.push(JSON.stringify({ role: role.name, entityId: space.id, entityType: space.spaceType })),
        );
        allNewRoles.push(space);
      });

    try {
      this.userDialogService.setStage(UserDialogStage.updating_roles);
      let i = 0;
      for (const spaceRole of spaceRolesToSubmit) {
        if (bulkRoles !== undefined && bulkRoles === true) {
          activeLocationRoles.sendProgressToDialog(i);
          i++;
        }

        submittedRolesStatus.set(spaceRole, 'LOADING');
        this.businessRolesService.submittedRolesStatus$.next(submittedRolesStatus);
        const { role, entityId, entityType } = JSON.parse(spaceRole);

        await this.businessRolesService
          .submitRoles(userId, entityId, role, entityType, userData.contact_id)
          .then((values) => {
            submittedRolesStatus.set(spaceRole, 'SUCCESS');
            this.businessRolesService.submittedRolesStatus$.next(submittedRolesStatus);
            console.log('No Errors:', values);
          })
          .catch((err) => {
            submittedRolesStatus.set(spaceRole, 'FAILED');
            err.role = spaceRole;
            errors.push(err);
            this.businessRolesService.submittedRolesStatus$.next(submittedRolesStatus);
            console.log('Could not submit role', err);
          });
      }
      if (bulkRoles !== undefined && bulkRoles === true) {
        activeLocationRoles.sendProgressToDialog(i);
        i++;
      }
      if (bulkRoles == undefined || bulkRoles === false) {
        const rolesToDelete: RoleToDelete[] = [];
        activeLocationRoles.activeLocations.forEach((space) => {
          space.spaceWithRoles.rolesToDelete.forEach((role) => {
            rolesToDelete.push({ entity: space.spaceWithRoles, role: role });
          });
        });
        const errorRolesDel: RoleToDelete[] = [];
        for (const role of rolesToDelete) {
          try {
            await this.businessRolesService.deleteRole(userId, role.entity.id, role.role.businessRole, '', '');
          } catch (e) {
            errorRolesDel.push(role);
            errors.push(e);
            console.log(e);
          }
        }
        activeLocationRoles.resetRoles();
        if (errorRolesDel.length > 0) {
          activeLocationRoles.errorRoles(activeLocationRoles.errorRoleArray.concat(errorRolesDel));
        }
        this.businessRolesService.removedAllRoles(rolesToDelete.map((role) => role.role));
      }
      this.businessRolesService.getUserRoles(userId);
      this.contactSubscriptionService.getContactSubscriptions(userId);
      this.applicationsService.getApplicationsByUser(userId);
      configureLocationRoles.deselectAll();
      if (subPromise) {
        const subErrors = await subPromise;
        if (subErrors.length > 0) {
          errors.push(subErrors);
        }
      }
      if (errors.length > 0) {
        throw errors;
      }
      return;
    } catch (error) {
      console.log(error);
      if (bulkRoles !== undefined && bulkRoles === true) activeLocationRoles.sendErrorToDialog();
      this.applicationsService.getApplicationsByUser(userId);
      this.contactSubscriptionService.getContactSubscriptions(userId);
      this.businessRolesService.getUserRoles(userId);
      this.businessRolesService.clearNewSpaces$.next(true);
      this.businessRolesService.newSpacesWithRoles$.next(allNewRoles);
      throw error;
    }
  }

  async newSubmitRoles(
    userId: string,
    activeLocationRoles: any,
    configureLocationRoles: any,
    user: any,
  ): Promise<void> {
    activeLocationRoles.sendUpdatingToDialog();

    const submittedRolesStatus = new Map();
    const rolesToSubmit: any[] = [];
    const errorRolesAdd: RoleToDelete[] = [];

    activeLocationRoles.activeLocations.forEach((location) => {
      rolesToSubmit.push(
        ...location.spaceWithRoles.newRoles.map((role) => {
          return { role: role, entity: location.spaceWithRoles };
        }),
      );
    });

    let i = 0;
    for (const role of rolesToSubmit) {
      submittedRolesStatus.set(role.role, 'LOADING');
      this.businessRolesService.submittedRolesStatus$.next(submittedRolesStatus);

      activeLocationRoles.sendProgressToDialog(i);
      i++;

      try {
        await this.businessRolesService.submitRoles(
          userId,
          role.entity.id,
          role.role.name,
          role.entity.spaceType,
          user.contact_id,
        );
        submittedRolesStatus.set(role.role, 'SUCCESS');
        this.businessRolesService.submittedRolesStatus$.next(submittedRolesStatus);
      } catch (e) {
        submittedRolesStatus.set(role.role, 'FAILED');
        errorRolesAdd.push(role);
        this.businessRolesService.submittedRolesStatus$.next(submittedRolesStatus);
        console.log('Could not submit role', e);
      }
    }
    activeLocationRoles.sendProgressToDialog(i);
    activeLocationRoles.resetRoles();
    if (errorRolesAdd.length > 0) {
      activeLocationRoles.errorRoles(activeLocationRoles.errorRoleArray.concat(errorRolesAdd));
      activeLocationRoles.sendErrorToDialog();
    } else {
      activeLocationRoles.sendSuccessToDialog();
    }
    await this.businessRolesService.getUserRoles(userId);
    if (errorRolesAdd.length > 0) {
      activeLocationRoles.addNewRoles(errorRolesAdd);
    }
    this.contactSubscriptionService.getContactSubscriptions(userId);
    this.applicationsService.getApplicationsByUser(userId);
    configureLocationRoles.deselectAll();
  }

  private async submitSubsImpl(
    userId: string,
    userData: any,
    subscriptionTable: SubscriptionTableComponent,
  ): Promise<void> {
    const subscriptions = subscriptionTable.subscriptionChanges();
    if (subscriptions.length > 0) this.userDialogService.setStage(UserDialogStage.updating_subs);
    const errors = [];
    const subsToAdd: ContactSubscriptionPost[] = [];
    const subsToDel: string[] = [];
    let errorCount = 0;
    let successCount = 0;

    for (const sub of subscriptions) {
      const subToAdd: ContactSubscriptionPost = {
        communication_name: '',
        site_id: sub.entityId,
        entity_type: sub.entityType,
        contact_methods: [],
        primary_ind: userData.primary_contact,
        twentyfour_seven_ind: userData.twentyfour_seven_contact,
        sync_to_ecrm: userData.sync_to_ecrm,
      };
      let auxSub;
      if (sub.sms && sub.sms == 'add') {
        auxSub = { ...subToAdd };
        auxSub.communication_name = 'NOTIFY_SMS';
        auxSub.contact_methods.push('sms');
        subsToAdd.push(auxSub);
      }
      if (sub.email && sub.email == 'add') {
        auxSub = { ...subToAdd };
        auxSub.communication_name = 'NOTIFY_EMAIL';
        auxSub.contact_methods.push('email');
        subsToAdd.push(auxSub);
      }
      if (sub.voice && sub.voice == 'add') {
        auxSub = { ...subToAdd };
        auxSub.communication_name = 'NOTIFY_PHONE';
        auxSub.contact_methods.push('voice');
        subsToAdd.push(auxSub);
      }
      if (sub.offer && sub.offer == 'add') {
        auxSub = { ...subToAdd };
        auxSub.communication_name = 'OFFER_EMAIL';
        !auxSub.contact_methods.some((method: string) => method === 'email')
          ? auxSub.contact_methods.push('email')
          : null;
        subsToAdd.push(auxSub);
      }
      if (sub.performance && sub.performance == 'add') {
        auxSub = { ...subToAdd };
        auxSub.communication_name = 'PERFORMANCE_EMAIL';
        !auxSub.contact_methods.some((method: string) => method === 'email')
          ? auxSub.contact_methods.push('email')
          : null;
        subsToAdd.push(auxSub);
      }

      if (sub.sms && sub.sms == 'del') {
        subsToDel.push(sub.smsSubscriptionId);
      }
      if (sub.email && sub.email == 'del') {
        subsToDel.push(sub.emailSubscriptionId);
      }
      if (sub.voice && sub.voice == 'del') {
        subsToDel.push(sub.voiceSubscriptionId);
      }
      if (sub.offer && sub.offer == 'del') {
        subsToDel.push(sub.offerSubscriptionId);
      }
      if (sub.performance && sub.performance == 'del') {
        subsToDel.push(sub.performanceSubscriptionId);
      }
    }
    if (!userData.flex_contact_id) {
      console.log('No contact id');
      errors.push('No contact id');
      throw errors;
    }

    const totalSubs = subsToAdd.length + subsToDel.length;
    const subscriptionProgress = `\nFailed: ${errorCount} - Succeeded: ${successCount} - Total: ${totalSubs}`;
    let i = 0;
    this.userDialogService.setSubscriptionProgress(subscriptionProgress);
    for (const sub of subsToAdd) {
      try {
        await this.contactSubscriptionService.postContactSubscription(userData.flex_contact_id, sub);
        i++;
        successCount++;
        this.userDialogService.setSubscriptionProgress(
          `\nFailed: ${errorCount} - Succeeded: ${successCount} - Total: ${totalSubs}`,
        );
      } catch (e) {
        console.log('Could not add subscription', e);
        errors.push(e);
        errorCount++;
        this.userDialogService.setSubscriptionProgress(
          `\nFailed: ${errorCount} - Succeeded: ${successCount} - Total: ${totalSubs}`,
        );
      }
    }
    for (const sub of subsToDel) {
      try {
        await this.contactSubscriptionService.deleteContactSubscription(userData.flex_contact_id, sub);
        i++;
        successCount++;
        this.userDialogService.setSubscriptionProgress(
          `\nFailed: ${errorCount} - Succeeded: ${successCount} - Total: ${totalSubs}`,
        );
      } catch (e) {
        console.log('Could not delete subscription', e);
        errors.push(e);
        errorCount++;
        this.userDialogService.setSubscriptionProgress(
          `\nFailed: ${errorCount} - Succeeded: ${successCount} - Total: ${totalSubs}`,
        );
      }
    }
    if (errors.length > 0) {
      await this.contactSubscriptionService.getContactSubscriptions(userId);
      throw errors;
    }
  }

  addSubscription(sub: string): void {
    switch (sub) {
      case 'sms':
        this.hasAnySMSNotificationRole = true;
        break;
      case 'email':
        this.hasAnyEmailNotificationRole = true;
        break;
      case 'voice':
        this.hasAnyVoiceNotificationRole = true;
        break;
      case 'offer':
        this.hasAnyOfferNotificationRole = true;
        break;
      case 'performance':
        this.hasAnyPerformanceNotificationRole = true;
        break;
    }
  }

  removeSubscription(sub: string): void {
    switch (sub) {
      case 'sms':
        this.hasAnySMSNotificationRole = false;
        break;
      case 'email':
        this.hasAnyEmailNotificationRole = false;
        break;
      case 'voice':
        this.hasAnyVoiceNotificationRole = false;
        break;
      case 'offer':
        this.hasAnyOfferNotificationRole = false;
        break;
      case 'performance':
        this.hasAnyPerformanceNotificationRole = false;
        break;
    }
  }

  setActiveLocationRoles(component: ActiveLocationRolesComponent) {
    this.activeLocationRoles$.next(component);
  }

  setConfigureLocationRoles(component: LocationRolesComponent) {
    this.configureLocationRoles$.next(component);
  }

  setSubscriptionTable(component: SubscriptionTableComponent) {
    this.subscriptionTable$.next(component);
  }

  resetInitialState() {
    this.hasAnyVoiceNotificationRole = false;
    this.hasAnySMSNotificationRole = false;
    this.hasAnyEmailNotificationRole = false;
    this.hasAnyOfferNotificationRole = false;
    this.hasAnyPerformanceNotificationRole = false;
    this.hasDrUser = false;
  }

  private getRequiredComponents() {
    const activeLocationRoles = this.activeLocationRoles$.value;
    const configureLocationRoles = this.configureLocationRoles$.value;
    const subscriptionTable = this.subscriptionTable$.value;

    if (!activeLocationRoles || !configureLocationRoles || !subscriptionTable) {
      throw new Error('Required components not initialized');
    }

    return { activeLocationRoles, configureLocationRoles, subscriptionTable };
  }
}
